// SPDX-License-Identifier: AGPL-3.0-or-later

// Badge collection modal: labelled badge grid on the left; on the right a lit showcase of the
// selected badge, what it is for, how it levels up and the full tier ladder.

import {Routes} from '@app/app/Routes';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {
	type AchievementProgress,
	type AchievementTier,
	getTierRarity,
	RARITY_LABELS,
} from '@app/features/user/achievements/AchievementCatalog';
import {AchievementBadgeArt, ladderColor, ladderVars} from '@app/features/user/achievements/AchievementBadgeArt';
import {useUserAchievements} from '@app/features/user/achievements/AchievementStats';
import styles from '@app/features/user/achievements/BadgeCollectionModal.module.css';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {formatNumber} from '@pkgs/number_utils/src/NumberFormatting';
import {CheckIcon, LockSimpleIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo, useState} from 'react';

/** A non-progression badge (staff, founding member…) shown alongside achievements. */
export interface SpecialBadgeEntry {
	key: string;
	title: string;
	description?: string;
	accent?: string;
	renderIcon: (size: number) => React.ReactNode;
}

type CollectionItem =
	| {kind: 'special'; key: string; badge: SpecialBadgeEntry}
	| {kind: 'achievement'; key: string; progress: AchievementProgress};

const OWN_TITLE_DESCRIPTOR = msg({message: 'Your badges', comment: 'Title of the badge collection modal for yourself.'});
const OTHER_TITLE_DESCRIPTOR = msg({
	message: "{name}'s badges",
	comment: "Title of the badge collection modal for another user. Preserve {name}; it is the user's display name.",
});
const LOCKED_SECTION_DESCRIPTOR = msg({
	message: 'Up for grabs',
	comment: 'Section in the badge modal listing badge categories the user has not unlocked yet.',
});
const VIEW_MINE_DESCRIPTOR = msg({message: 'Show my badges', comment: 'Button in the badge modal that switches to your own collection.'});
const BACK_DESCRIPTOR = msg({
	message: 'Back to {name}',
	comment: "Button in the badge modal returning to the other user's collection. Preserve {name}.",
});
const CLOSE_DESCRIPTOR = msg({message: 'Close', comment: 'Accessible label of the badge modal close button.'});
const ABOUT_HEADING_DESCRIPTOR = msg({message: 'What it is for', comment: 'Section heading in the badge modal.'});
const HOW_HEADING_DESCRIPTOR = msg({message: 'How it levels up', comment: 'Section heading in the badge modal.'});
const OWN_PROGRESS_HEADING_DESCRIPTOR = msg({message: 'Your progress', comment: 'Section heading above the tier cards.'});
const OTHER_PROGRESS_HEADING_DESCRIPTOR = msg({
	message: "{name}'s progress",
	comment: 'Section heading above the tier cards for another user. Preserve {name}.',
});
const MAXED_DESCRIPTOR = msg({
	message: 'Top level reached.',
	comment: 'Shown in the badge modal when the highest tier is reached.',
});
const NEXT_IN_DESCRIPTOR = msg({
	message: 'Next level in {amount}.',
	comment: 'Badge modal: remaining amount to the next tier. Preserve {amount} (e.g. "55 days").',
});
const SINCE_DESCRIPTOR = msg({
	message: 'Since {date}',
	comment: 'Badge modal stat line: when the measured period started. Preserve {date}.',
});
const NOT_STARTED_DESCRIPTOR = msg({message: 'Not unlocked yet', comment: 'Badge modal stat line for a locked category.'});
const EARNED_DESCRIPTOR = msg({message: 'Earned', comment: 'Status of a reached tier card in the badge modal.'});
const CURRENT_DESCRIPTOR = msg({message: 'Current', comment: 'Status of the current tier card in the badge modal.'});
const LOCKED_DESCRIPTOR = msg({message: 'Locked', comment: 'Status of a locked tier card in the badge modal.'});
const FIRST_DAY_DESCRIPTOR = msg({message: 'First day', comment: 'Tier threshold label for a tier reached on day one.'});
const DAYS_DESCRIPTOR = msg({message: '{count} days', comment: 'Badge stat. Preserve {count}.'});
const MONTHS_DESCRIPTOR = msg({message: '{count} mo', comment: 'Short tier threshold in months. Preserve {count}.'});
const YEARS_DESCRIPTOR = msg({message: '{count} yr', comment: 'Short tier threshold in years. Preserve {count}.'});
const WEEK_DESCRIPTOR = msg({message: '1 week', comment: 'Tier threshold of seven days.'});
const HOURS_DESCRIPTOR = msg({message: '{count} hours', comment: 'Badge stat. Preserve {count}.'});
const GAMES_DESCRIPTOR = msg({message: '{count} games', comment: 'Badge stat. Preserve {count}.'});
const SPECIAL_KICKER_DESCRIPTOR = msg({message: 'Special badge', comment: 'Chip on a non-progression badge in the badge modal.'});
const GET_PRO_DESCRIPTOR = msg({message: 'Get Pro', comment: 'Call to action in the badge modal for the Pro badge.'});

function formatCount(i18n: I18n, unit: AchievementProgress['category']['unit'], value: number): string {
	const count = formatNumber(value, getCurrentLocale());
	if (unit === 'hours') return i18n._(HOURS_DESCRIPTOR, {count});
	if (unit === 'games') return i18n._(GAMES_DESCRIPTOR, {count});
	return i18n._(DAYS_DESCRIPTOR, {count});
}

/** Compact ladder label: "First day", "1 week", "3 mo", "2 yr", "500 hours". */
function formatThreshold(i18n: I18n, unit: AchievementProgress['category']['unit'], tier: AchievementTier): string {
	if (unit !== 'days') return formatCount(i18n, unit, tier.threshold);
	const days = tier.threshold;
	if (days === 0) return i18n._(FIRST_DAY_DESCRIPTOR);
	if (days === 7) return i18n._(WEEK_DESCRIPTOR);
	if (days >= 365 && days % 365 === 0) return i18n._(YEARS_DESCRIPTOR, {count: days / 365});
	if (days >= 30) return i18n._(MONTHS_DESCRIPTOR, {count: Math.round(days / 30)});
	return formatCount(i18n, unit, days);
}

interface BadgeCollectionModalProps {
	user: User;
	profile: Profile | null;
	specialBadges: ReadonlyArray<SpecialBadgeEntry>;
	/** Builds the viewer's own special badges for the "Show my badges" switch. */
	getOwnSpecialBadges?: () => ReadonlyArray<SpecialBadgeEntry>;
	initialKey?: string;
}

export const BadgeCollectionModal = observer(function BadgeCollectionModal({
	user,
	profile,
	specialBadges,
	getOwnSpecialBadges,
	initialKey,
}: BadgeCollectionModalProps) {
	const {i18n} = useLingui();
	const me = Users.currentUser;
	const ownSpecialBadges = useMemo(() => getOwnSpecialBadges?.() ?? [], [getOwnSpecialBadges, i18n.locale]);
	const [showMine, setShowMine] = useState(false);
	const viewingSelf = me != null && (showMine || me.id === user.id);
	const subject = viewingSelf && me ? me : user;
	const title = viewingSelf ? i18n._(OWN_TITLE_DESCRIPTOR) : i18n._(OTHER_TITLE_DESCRIPTOR, {name: user.displayName});
	return (
		<Modal.Root size="large" className={styles.root} data-flx="user.badge-collection-modal">
			<Modal.ScreenReaderLabel text={title} />
			<CollectionBody
				key={subject.id}
				user={subject}
				title={title}
				profile={subject.id === user.id ? profile : null}
				specialBadges={subject.id === user.id ? specialBadges : ownSpecialBadges}
				initialKey={subject.id === user.id ? initialKey : undefined}
				isSelf={viewingSelf}
				footer={
					me && me.id !== user.id ? (
						<button type="button" className={styles.footerButton} onClick={() => setShowMine((value) => !value)}>
							{showMine ? i18n._(BACK_DESCRIPTOR, {name: user.displayName}) : i18n._(VIEW_MINE_DESCRIPTOR)}
						</button>
					) : null
				}
			/>
		</Modal.Root>
	);
});

interface CollectionBodyProps {
	user: User;
	title: string;
	profile: Profile | null;
	specialBadges: ReadonlyArray<SpecialBadgeEntry>;
	initialKey?: string;
	isSelf: boolean;
	footer: React.ReactNode;
}

const CollectionBody = observer(function CollectionBody({
	user,
	title,
	profile,
	specialBadges,
	initialKey,
	isSelf,
	footer,
}: CollectionBodyProps) {
	const {i18n} = useLingui();
	const achievements = useUserAchievements(user, profile ?? null);
	const earned = useMemo<Array<CollectionItem>>(
		() => [
			...achievements
				.filter((progress) => progress.tierIndex >= 0)
				.map((progress) => ({kind: 'achievement' as const, key: progress.category.id, progress})),
			...specialBadges.map((badge) => ({kind: 'special' as const, key: badge.key, badge})),
		],
		[specialBadges, achievements],
	);
	const locked = useMemo<Array<CollectionItem>>(
		() =>
			isSelf
				? achievements
						.filter((progress) => progress.tierIndex < 0)
						.map((progress) => ({kind: 'achievement' as const, key: progress.category.id, progress}))
				: [],
		[achievements, isSelf],
	);
	const [selectedKey, setSelectedKey] = useState<string | undefined>(initialKey);
	const all = [...earned, ...locked];
	const selected = all.find((item) => item.key === selectedKey) ?? all[0] ?? null;
	const renderTile = (item: CollectionItem) => {
		const label = item.kind === 'special' ? item.badge.title.split('\n')[0] : i18n._(item.progress.category.title);
		const accent =
			item.kind === 'special'
				? (item.badge.accent ?? 'var(--brand-primary)')
				: ladderColor(item.progress.category.id, Math.max(0, item.progress.tierIndex), item.progress.category.tiers.length);
		return (
			<button
				key={item.key}
				type="button"
				className={clsx(styles.tile, selected?.key === item.key && styles.tileSelected)}
				style={{'--tile-accent': accent} as React.CSSProperties}
				onClick={() => setSelectedKey(item.key)}
				aria-pressed={selected?.key === item.key}
			>
				<span className={styles.tileIcon}>
					{item.kind === 'special' ? (
						item.badge.renderIcon(36)
					) : (
						<AchievementBadgeArt
							categoryId={item.progress.category.id}
							tierIndex={Math.max(0, item.progress.tierIndex)}
							tierCount={item.progress.category.tiers.length}
							size={42}
							locked={item.progress.tierIndex < 0}
						/>
					)}
					{item.kind === 'achievement' && item.progress.tierIndex < 0 ? (
						<span className={styles.rungMark}>
							<LockSimpleIcon size={9} weight="fill" />
						</span>
					) : null}
				</span>
				<span className={styles.tileLabel}>{label}</span>
			</button>
		);
	};
	return (
		<div className={styles.layout}>
			<aside className={styles.sidebar}>
				<h2 className={styles.title}>{title}</h2>
				<div className={styles.tiles}>{earned.map(renderTile)}</div>
				{locked.length > 0 ? (
					<>
						<h3 className={styles.sectionLabel}>{i18n._(LOCKED_SECTION_DESCRIPTOR)}</h3>
						<div className={styles.tiles}>{locked.map(renderTile)}</div>
					</>
				) : null}
				<div className={styles.sidebarSpacer} />
				{footer}
			</aside>
			<section className={styles.detail}>
				<button type="button" className={styles.close} onClick={() => ModalCommands.pop()} aria-label={i18n._(CLOSE_DESCRIPTOR)}>
					<XIcon size={18} weight="bold" />
				</button>
				{selected?.kind === 'special' ? <SpecialDetail badge={selected.badge} /> : null}
				{selected?.kind === 'achievement' ? (
					<AchievementDetail progress={selected.progress} isSelf={isSelf} ownerName={user.displayName} />
				) : null}
			</section>
		</div>
	);
});

function Hero({vars, children}: {vars: Record<string, string>; children: React.ReactNode}) {
	return (
		<div className={styles.hero} style={vars as React.CSSProperties}>
			<div className={styles.heroGlow} />
			<div className={styles.heroFloor} />
			<div className={styles.heroArt}>{children}</div>
		</div>
	);
}

function SpecialDetail({badge}: {badge: SpecialBadgeEntry}) {
	const {i18n} = useLingui();
	const [headline, ...rest] = badge.title.split('\n');
	return (
		<>
			<Hero vars={{'--badge-accent': badge.accent ?? 'var(--brand-primary)'}}>{badge.renderIcon(124)}</Hero>
			<div className={styles.heading}>
				<h3 className={styles.badgeTitle}>{headline}</h3>
				<div className={styles.chips}>
					<span className={styles.chip}>{i18n._(SPECIAL_KICKER_DESCRIPTOR)}</span>
				</div>
				{rest.length > 0 ? <p className={styles.statLine}>{rest.join(' ')}</p> : null}
			</div>
			{badge.description ? (
				<div className={styles.section}>
					<h4 className={styles.sectionLabel}>{i18n._(ABOUT_HEADING_DESCRIPTOR)}</h4>
					<p className={styles.body}>{badge.description}</p>
				</div>
			) : null}
		</>
	);
}

const AchievementDetail = observer(function AchievementDetail({
	progress,
	isSelf,
	ownerName,
}: {
	progress: AchievementProgress;
	isSelf: boolean;
	ownerName: string;
}) {
	const {i18n} = useLingui();
	const {category, tierIndex, value, since} = progress;
	const reached = tierIndex >= 0;
	const shownTier = Math.max(0, tierIndex);
	const tierCount = category.tiers.length;
	const next = category.tiers[tierIndex + 1] ?? null;
	const previousThreshold = reached ? category.tiers[tierIndex].threshold : 0;
	const ratio = next
		? Math.min(1, Math.max(0, (Math.max(0, value) - previousThreshold) / Math.max(1, next.threshold - previousThreshold)))
		: 1;
	const accent = ladderColor(category.id, shownTier, tierCount);
	const rarity = getTierRarity(category, shownTier);
	const statLine = !reached
		? i18n._(NOT_STARTED_DESCRIPTOR)
		: since && category.unit === 'days'
			? i18n._(SINCE_DESCRIPTOR, {date: DateUtils.getFormattedShortDate(since)})
			: formatCount(i18n, category.unit, value);
	const remaining = next ? formatCount(i18n, category.unit, Math.max(0, next.threshold - Math.max(0, value))) : null;
	const viewerIsPro = Users.currentUser?.isPremium() ?? false;
	return (
		<>
			<Hero vars={ladderVars(category.id, shownTier, tierCount)}>
				<AchievementBadgeArt categoryId={category.id} tierIndex={shownTier} tierCount={tierCount} size={140} locked={!reached} />
			</Hero>
			<div className={styles.heading}>
				<h3 className={styles.badgeTitle}>{i18n._(category.title)}</h3>
				<div className={styles.chips}>
					<span className={styles.chip} style={{'--chip-accent': accent} as React.CSSProperties}>
						{i18n._(category.tiers[shownTier].name)}
					</span>
					{reached ? (
						<span className={clsx(styles.chip, styles.rarityChip, styles[`rarity_${rarity}`])}>
							{i18n._(RARITY_LABELS[rarity])}
						</span>
					) : null}
				</div>
				<p className={styles.statLine}>{statLine}</p>
			</div>
			<div className={styles.section}>
				<h4 className={styles.sectionLabel}>{i18n._(ABOUT_HEADING_DESCRIPTOR)}</h4>
				<p className={styles.body}>{i18n._(category.about)}</p>
			</div>
			<div className={styles.section}>
				<h4 className={styles.sectionLabel}>{i18n._(HOW_HEADING_DESCRIPTOR)}</h4>
				<p className={styles.body}>
					{i18n._(category.hint)} {remaining ? i18n._(NEXT_IN_DESCRIPTOR, {amount: remaining}) : i18n._(MAXED_DESCRIPTOR)}
				</p>
				{isSelf && next ? (
					<div className={styles.progressTrack} aria-hidden="true">
						<div
							className={styles.progressFill}
							style={{width: `${Math.round(ratio * 100)}%`, '--badge-accent': accent} as React.CSSProperties}
						/>
					</div>
				) : null}
			</div>
			<div className={styles.section}>
				<h4 className={styles.sectionLabel}>
					{isSelf ? i18n._(OWN_PROGRESS_HEADING_DESCRIPTOR) : i18n._(OTHER_PROGRESS_HEADING_DESCRIPTOR, {name: ownerName})}
				</h4>
				<ol className={styles.ladder}>
					{category.tiers.map((tier, index) => {
						const state = index < tierIndex ? 'earned' : index === tierIndex ? 'current' : 'locked';
						const tierRarity = getTierRarity(category, index);
						const status =
							state === 'earned'
								? i18n._(EARNED_DESCRIPTOR)
								: state === 'current'
									? i18n._(CURRENT_DESCRIPTOR)
									: i18n._(LOCKED_DESCRIPTOR);
						return (
							<li
								key={tier.threshold}
								className={clsx(styles.rung, state === 'current' && styles.rungCurrent)}
								style={{'--badge-accent': ladderColor(category.id, index, tierCount)} as React.CSSProperties}
							>
								<span className={styles.rungArt}>
									<AchievementBadgeArt
										categoryId={category.id}
										tierIndex={index}
										tierCount={tierCount}
										size={40}
										locked={state === 'locked'}
									/>
									{state === 'earned' ? (
										<span className={clsx(styles.rungMark, styles.rungMarkEarned)}>
											<CheckIcon size={9} weight="bold" />
										</span>
									) : null}
									{state === 'locked' ? (
										<span className={styles.rungMark}>
											<LockSimpleIcon size={9} weight="fill" />
										</span>
									) : null}
								</span>
								<span className={clsx(styles.rungName, state === 'locked' && styles.rungDim)}>{i18n._(tier.name)}</span>
								<span className={styles.rungThreshold}>{formatThreshold(i18n, category.unit, tier)}</span>
								<span className={styles.rungStatus}>
									{status} · {i18n._(RARITY_LABELS[tierRarity])}
								</span>
							</li>
						);
					})}
				</ol>
			</div>
			{category.cta === 'premium' && !viewerIsPro ? (
				<div className={styles.ctaRow}>
					<button
						type="button"
						className={styles.ctaButton}
						onClick={() => {
							ModalCommands.pop();
							RouterUtils.transitionTo(Routes.PREMIUM);
						}}
					>
						{i18n._(GET_PRO_DESCRIPTOR)}
					</button>
				</div>
			) : null}
		</>
	);
});

export function openBadgeCollection(props: BadgeCollectionModalProps): void {
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => <BadgeCollectionModal {...props} />),
		`badge-collection-${props.user.id}`,
	);
}
